import React, { useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { parApi, parErr } from "@/lib/partnerApi";
import { useAuth } from "@/lib/auth";
import { inputCls, GoldButton } from "@/components/nds/windows/resident/ui";
import { Chip } from "@/components/nds/windows/finance/parts";
import { QuestionnaireEditor } from "@/components/nds/windows/questionnaire/QuestionnaireEditor";
import { ProspectWelcome } from "@/components/nds/partnerWelcome/ProspectWelcome";
import { PlayCircle } from "lucide-react";
import { GhostButton } from "@/components/nds/windows/resident/ui";

const REF = { submitted: ["yellow", "Submitted"], reviewing: ["blue", "Under review"], accepted: ["green", "Accepted"], declined: ["red", "Declined"], withdrawn: ["grey", "Withdrawn"] };
const CONSENT = { signed: ["green", "Applicant signed"], sent: ["yellow", "Awaiting applicant"], not_sent: ["grey", "Consent not sent"] };

/** Referral questionnaires: partners see theirs, staff see all. */
export default function QuestionnaireTab() {
  const { user } = useAuth();
  const staff = user?.role !== "partner_user";
  const [schema, setSchema] = useState(null);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const [play, setPlay] = useState(false);
  const load = () => parApi.get("/questionnaires").then((d) => setRows(d.questionnaires)).catch((e) => toast.error(parErr(e)));
  useEffect(() => { parApi.get("/questionnaires/schema").then(setSchema); load(); }, []);
  const create = async () => { try { const d = await parApi.post("/questionnaires", {}); setOpen(d.id); } catch (e) { toast.error(parErr(e)); } };
  if (!schema || !rows) return <p className="text-sm text-nex-muted">Loading…</p>;
  if (open) return <QuestionnaireEditor id={open} schema={schema} onBack={() => { setRows(null); setOpen(null); load(); }} />;
  const t = q.trim().toLowerCase();
  const list = rows.filter((r) => !t || `${r.applicant} ${r.partner_name || ""} ${r.agency}`.toLowerCase().includes(t));
  return (
    <div data-testid="q-list" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-nex-muted flex-1 min-w-[200px]">{staff ? "Every referral questionnaire from partners. Accepted referrals carry their answers into the resident's intake." : "Refer someone by completing the intake & referral questionnaire. You can save a draft and edit until TEA starts reviewing."}</p>
        <GhostButton data-testid="q-welcome-preview" onClick={() => setPlay(true)}><PlayCircle className="h-3 w-3" /> Resident welcome</GhostButton>
        <GoldButton data-testid="q-new" onClick={create}><Plus className="h-3 w-3" /> New questionnaire</GoldButton>
      </div>
      {play && <ProspectWelcome replay onDone={() => setPlay(false)} onClose={() => setPlay(false)} />}
      <div className="relative"><Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-nex-muted" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search applicant, partner or agency" data-testid="q-search" className={`${inputCls} pl-8`} /></div>
      {!list.length ? <p data-testid="q-empty" className="py-8 text-center text-sm text-nex-muted">No questionnaires yet.</p> : (
        <ul className="divide-y divide-white/5 border border-white/10 rounded-sm">{list.map((r) => {
          const [rt, rl] = r.status === "draft" ? ["grey", "Draft"] : REF[r.referral_status] || ["grey", r.referral_status];
          const [ct, cl] = CONSENT[r.consent] || CONSENT.not_sent;
          return (
            <li key={r.id}><button type="button" onClick={() => setOpen(r.id)} data-testid={`q-row-${r.id}`} className="w-full text-left px-4 py-3 flex flex-wrap items-center gap-2 hover:bg-white/[0.03]">
              <div className="flex-1 min-w-[180px]"><div className="text-nex-silver font-medium">{r.applicant}</div>
                <div className="text-xs text-nex-muted">{[r.agency, staff && r.partner_name, r.updated_at && `updated ${r.updated_at.slice(0, 10)}`].filter(Boolean).join(" · ")}</div></div>
              <Chip tone={rt}>{rl}</Chip>
              {r.status === "submitted" && <Chip tone={ct}>{cl}</Chip>}
              {staff && r.status === "submitted" && <Chip tone={r.pmc_done === 3 ? "green" : "grey"}>PMC {r.pmc_done}/3</Chip>}
              {r.watched && <Chip tone="blue">Watched welcome</Chip>}
              {r.resident_id && <Chip tone="green">Resident</Chip>}
            </button></li>);
        })}</ul>)}
    </div>
  );
}
